import * as React from "react";
import TextField, { TextFieldProps } from "@mui/material/TextField";
import { styled, alpha } from "@mui/material/styles";
import { Box } from "@mui/material";
import CardBase from "./CardBase";

const StyledTextFieldInput = styled(TextField)(({ theme }) => {
    const isDark = theme.palette.mode === "dark";
    return {
        "& .MuiOutlinedInput-root": {
            borderRadius: "12px !important",
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            position: "relative",
            // Background sangat transparan dengan gradien putih murni
            background: isDark
                ? `linear-gradient(135deg, ${alpha("#ffffff", 0.15)} 0%, ${alpha("#ffffff", 0.08)} 100%)`
                : `linear-gradient(135deg, ${alpha("#ffffff", 0.25)} 0%, ${alpha("#ffffff", 0.15)} 100%)`,
            backdropFilter: "blur(40px) saturate(180%)",
            WebkitBackdropFilter: "blur(40px) saturate(180%)",
            // Border putih yang jelas
            border: `1.5px solid ${isDark ? alpha("#ffffff", 0.4) : alpha("#ffffff", 0.8)}`,
            boxShadow: isDark
                ? "0 8px 32px rgba(0, 0, 0, 0.3), 0 2px 8px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.3)"
                : "0 8px 32px rgba(18, 144, 60, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.6)",
            overflow: "hidden",
            // Responsive for mobile landscape
            "@media (orientation: landscape) and (max-width: 900px)": {
                borderRadius: "8px !important",
                minHeight: "32px",
                padding: "4px 8px",
                scrollMarginTop: 0,
                "&.Mui-focused": {
                    scrollMarginTop: 0,
                },
                "& .MuiInputBase-input": {
                    fontSize: "0.8125rem",
                    padding: "6px 0",
                },
            },
            "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: "50%",
                background: `linear-gradient(180deg, ${alpha("#ffffff", isDark ? 0.15 : 0.25)} 0%, transparent 100%)`,
                pointerEvents: "none",
                zIndex: 0,
            },
            "&::after": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: `linear-gradient(135deg, transparent 0%, transparent 100%)`,
                pointerEvents: "none",
                zIndex: 0,
            },
            "&:hover": {
                transform: "translateY(-2px)",
                boxShadow: isDark
                    ? "0 12px 48px rgba(0, 0, 0, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.4)"
                    : "0 12px 48px rgba(18, 144, 60, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.7)",
                border: `1.5px solid ${isDark ? alpha("#ffffff", 0.5) : alpha("#ffffff", 0.9)}`,
                background: isDark
                    ? `linear-gradient(135deg, ${alpha("#ffffff", 0.18)} 0%, ${alpha("#ffffff", 0.1)} 100%)`
                    : `linear-gradient(135deg, ${alpha("#ffffff", 0.3)} 0%, ${alpha("#ffffff", 0.18)} 100%)`,
            },
            "&.Mui-focused": {
                transform: "translateY(-2px)",
                boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.2)}, inset 0 1px 0 rgba(255, 255, 255, 0.7)`,
                border: `1.5px solid ${alpha("#ffffff", 0.95)}`,
                background: isDark
                    ? `linear-gradient(135deg, ${alpha("#ffffff", 0.2)} 0%, ${alpha("#ffffff", 0.12)} 100%)`
                    : `linear-gradient(135deg, ${alpha("#ffffff", 0.35)} 0%, ${alpha("#ffffff", 0.22)} 100%)`,
            },
            "& .MuiInputBase-input": {
                position: "relative",
                zIndex: 1,
                // Responsive font size for mobile landscape
                "@media (orientation: landscape) and (max-width: 900px)": {
                    fontSize: "0.8125rem",
                },
            },
        },
        "& .MuiOutlinedInput-notchedOutline": {
            borderRadius: "12px",
            border: "none",
            "@media (orientation: landscape) and (max-width: 900px)": {
                borderRadius: "8px",
            },
        },
        "& .MuiInputLabel-root": {
            zIndex: 1,
            left: theme.spacing(1.4),
            top: theme.spacing(0.9),
            padding: 0,
            color: theme.palette.text.secondary,
            background: "transparent",
            transformOrigin: "top left",
            transition: "all 0.2s ease",
            fontSize: { xs: "0.85rem", sm: "0.95rem" },
            fontWeight: 500,
            [theme.breakpoints.down("sm")]: {
                maxWidth: "calc(100% - 32px)",
                wordBreak: "break-word",
                whiteSpace: "normal",
            },
            // Responsive for mobile landscape
            "@media (orientation: landscape) and (max-width: 900px)": {
                fontSize: "0.8125rem",
                left: theme.spacing(1),
                top: theme.spacing(0.75),
            },
            "&.Mui-focused": {
                color: theme.palette.primary.main,
            },
            "&.MuiInputLabel-shrink": {
                top: theme.spacing(-1.2),
                transform: "translateY(0) scale(0.86)",
                padding: theme.spacing(0, 0.75),
                borderRadius: 999,
                width: "fit-content",
                maxWidth: { xs: "85%", sm: "90%" },
                display: { xs: "block", sm: "inline-flex" },
                fontSize: { xs: "0.78rem", sm: "0.88rem" },
                fontWeight: 500,
                color: "#0f6d2b",
                background: theme.palette.mode === "dark"
                    ? alpha("#ffffff", 0.15)
                    : alpha("#ffffff", 0.98),
                boxShadow: theme.palette.mode === "dark"
                    ? "0 1px 4px rgba(0,0,0,0.35)"
                    : "0 1px 4px rgba(0,0,0,0.15)",
                [theme.breakpoints.down("sm")]: {
                    whiteSpace: "nowrap",
                    wordBreak: "break-word",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    display: "block",
                    lineHeight: 1.3,
                    width: "auto",
                },
                // Responsive for mobile landscape
                "@media (orientation: landscape) and (max-width: 900px)": {
                    fontSize: "0.75rem",
                    top: theme.spacing(-1),
                    transform: "translateY(0) scale(0.8)",
                    padding: theme.spacing(0, 0.5),
                },
            },
        },
    };
});

interface StyledTextFieldProps extends Omit<TextFieldProps, 'component'> {
    useGlassCard?: boolean;
    glassCardTitle?: string;
    glassCardSubtitle?: string;
}

const StyledTextField: React.FC<StyledTextFieldProps> = ({
    useGlassCard = false,
    glassCardTitle,
    glassCardSubtitle,
    ...textFieldProps
}) => {
    if (useGlassCard) {
        return (
            <CardBase
                title={glassCardTitle}
                subtitle={glassCardSubtitle}
                useGlassCard={true}
                sx={{ p: 0 }}
            >
                <Box sx={{ p: 3 }}>
                    <StyledTextFieldInput {...textFieldProps} />
                </Box>
            </CardBase>
        );
    }

    return <StyledTextFieldInput {...textFieldProps} />;
};

export default StyledTextField;

